import { useEffect } from "react";
import { useLensApp } from "../useLensApp.jsx";
import ApiCallsSection from "./ApiCallsSection.jsx";
import ApiSection from "./ApiSection.jsx";
import LogSection from "./LogSection.jsx";
import RemoteControlSection from "./RemoteControlSection.jsx";

function Diagnostics() {
  const {
    cameraMode, lastError, runtimeStatus, settings,
    telemetryLogText, virtualCameraStatusText, sessionLogInfo, actions,
  } = useLensApp();
  return (
    <>
      <section>
        <h3>Current state</h3>
        <dl className="diagnostic-grid">
          <dt>Camera mode</dt><dd>{cameraMode}</dd>
          <dt>Native runtime</dt><dd>{runtimeStatus.available ? "Connected" : "Unavailable"}</dd>
          <dt>Runtime endpoint</dt><dd>{runtimeStatus.endpoint || "—"}</dd>
          <dt>Virtual camera</dt><dd>{virtualCameraStatusText}</dd>
          <dt>Local API</dt><dd>{settings?.apiBaseUrl || settings?.apiLastError || "Off"}</dd>
        </dl>
        {lastError && <div className="inline-error utility-error">{lastError.message}</div>}
      </section>
      <section>
        <div className="section-heading-row">
          <div>
            <h3>Session log</h3>
            <div className="hint flush">
              Renderer telemetry, browser warnings, and control errors are written here until the app exits.
            </div>
          </div>
          <button
            className="compact-action"
            disabled={!sessionLogInfo?.available}
            onClick={() => actions.openSessionLog().catch(actions.setError)}
          >
            Open log
          </button>
        </div>
        <code className="session-log-path">{sessionLogInfo?.path || "Available in the desktop app"}</code>
      </section>
      <section>
        <h3>Renderer capability</h3>
        <div className="field">
          <label htmlFor="rendererDeviceClass">LensCore device class</label>
          <select
            id="rendererDeviceClass"
            value={settings?.rendererDeviceClass || "undefined"}
            onChange={(event) => actions.saveRendererSettings({
              rendererDeviceClass: event.target.value,
            }).catch(actions.setError)}
          >
            <option value="undefined">Automatic / undefined</option>
            <option value="barely-working">Barely working</option>
            <option value="low-end">Low end</option>
            <option value="mid-end">Mid end</option>
            <option value="high-end">High end</option>
          </select>
          <div className="hint">
            Stored on this computer. Changing it updates Preview immediately and restarts the native renderer when Virtual Camera is active.
          </div>
        </div>
      </section>
      <section>
        <div className="section-heading-row">
          <div>
            <h3>Native runtime</h3>
            <div className="hint flush">{telemetryLogText}</div>
          </div>
          <button
            className="danger compact-action"
            onClick={() => actions.runVirtualCamera("remove", { virtualCameraEnabled: false }).catch(actions.setError)}
          >
            Uninstall virtual camera
          </button>
        </div>
        <pre className="diagnostic-json">{JSON.stringify(runtimeStatus, null, 2)}</pre>
      </section>
      <LogSection />
    </>
  );
}

function ApiHelp() {
  const { settings, actions } = useLensApp();
  return (
    <>
      <section>
        <h3>Local control boundary</h3>
        <p className="utility-copy">
          The desktop API binds to this machine only. Its default address is
          {" "}<code>http://127.0.0.1:4789</code>; the chosen port remains stable across launches.
        </p>
        <div className="button-row">
          <button onClick={() => actions.setCameraMode("off").catch(actions.setError)}>Release camera</button>
          <button onClick={() => actions.reportApiCalls().catch(actions.setError)}>Refresh endpoint list</button>
        </div>
        <div className="hint">Active address: {settings?.apiBaseUrl || settings?.apiLastError || "API is off"}</div>
      </section>
      <ApiSection />
      <RemoteControlSection />
      <ApiCallsSection />
    </>
  );
}

export default function UtilityDialog() {
  const { utilityPanel, actions } = useLensApp();

  useEffect(() => {
    if (!utilityPanel) return undefined;
    const close = (event) => { if (event.key === "Escape") actions.closeUtilityPanel(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [actions, utilityPanel]);

  if (!utilityPanel) return null;
  const api = utilityPanel === "api";
  return (
    <div className="utility-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) actions.closeUtilityPanel();
    }}>
      <div className="utility-dialog" role="dialog" aria-modal="true" aria-labelledby="utilityTitle">
        <header className="utility-header">
          <div>
            <h2 id="utilityTitle">{api ? "Local API & Remote Control" : "Diagnostics"}</h2>
            <div className="subtle">{api ? "Automation settings and endpoint reference" : "Runtime status and troubleshooting details"}</div>
          </div>
          <button className="icon-button" aria-label="Close" onClick={actions.closeUtilityPanel}>×</button>
        </header>
        <div className="utility-scroll">{api ? <ApiHelp /> : <Diagnostics />}</div>
      </div>
    </div>
  );
}
